import React, { useState, useEffect } from 'react';
import { Modal, Button, Popconfirm, message, Form, Input,Spin, Col, Row } from 'antd';
import { EditOutlined, DeleteOutlined} from '@ant-design/icons';
import { deleteTemplateDetails, UpdateTemplateDetails} from '../../../services/ReportTemplateService';
import { useReportTemplate, ReportTreeRenderer, buildColumnMapping } from './templateUtils';

const TemplateDetailsModal = ({ 
  visible, 
  onClose, 
  id, 
  type_name,
  onDelete,
  onEdit
}) => {
  const [form] = Form.useForm();
  
  const {
    loading,
    treeData,
    checkedColumns,
    filters,
    editMode,
    initialValues,
    onColumnReorder,
    onCheck,
    onFilterChange,
    onOrderChange,
    switchToEditMode,
    cancelEditMode,
    validateFilters } = useReportTemplate({ type_name, templateId: id, visible });

  // Set form values when initial values change
  useEffect(() => {
    if (initialValues) {
      form.setFieldsValue(initialValues);
    }
  }, [initialValues, form]);

  const handleFormDataUpdate = async () => {
    try {
      const values = await form.validateFields();
      const errors = validateFilters();
      if (errors.length > 0) {
        message.error(errors.join('\n'));
        return;
      }

      const payload = {
        template_name: values.templateName,
        description: values.description,
        column_mapping: buildColumnMapping(checkedColumns),
        col_filters: filters
      };
      
      await UpdateTemplateDetails(id, payload);
      message.success('Template updated successfully');
      cancelEditMode();
      
      if (onEdit) onEdit(id);
    } catch (error) {
      console.error('Error updating template:', error);
      message.error('Failed to update template');
    }
  };

  const handleDeleteTemplate = async () => {
    try {
      const response = await deleteTemplateDetails(id);
      
      if (response) {
        message.success('Template deleted successfully');
      }
      onClose();

      if (onDelete) onDelete(id);
    } catch (error) {
      console.error('Error deleting template:', error);
      message.error('Failed to delete template');
    }
  };



  return (
    <Modal
      title={type_name}
      open={visible}
      onCancel={onClose}
      width={1000}
      footer={[
        <Button key="close" onClick={onClose}>
          Close
        </Button>,
        <Popconfirm
          key="delete"
          title="Are you sure you want to delete this template?"
          onConfirm={handleDeleteTemplate}
          okText="Yes"
          cancelText="No"
        >
          <Button danger icon={<DeleteOutlined />}>
            Delete template
          </Button>
        </Popconfirm>,
        !editMode ? (
          <Button
            key="edit"
            type="primary"
            className="cip-btn-submit"
            icon={<EditOutlined />}
            onClick={switchToEditMode}
          >
            Edit
          </Button>
        ) : (
          <>
            <Button key="cancel" onClick={cancelEditMode}>
              Cancel
            </Button>
            <Button
              key="save"
              type="primary"
              className="cip-btn-submit"
              onClick={handleFormDataUpdate}
              loading={loading}
            >
              Save
            </Button>
          </>
        ),
      ]}   
    >
      <Form form={form} layout="vertical">
        <Row gutter={16}>
        <Col span={12}>
        <Form.Item
          label="Template Name"
          name="templateName"
          rules={[{ required: true, message: 'Please enter template name' }]}
        >
          <Input placeholder="Enter template name" disabled={!editMode} />
        </Form.Item>
        </Col>
        <Col span={12}>
        <Form.Item label="Description" name="description">
          <Input placeholder="Enter description" disabled={!editMode} />
        </Form.Item>
        </Col>
        </Row>
      </Form>

      {loading ? (
        <Spin />
      ) : (
        <ReportTreeRenderer
          treeData={treeData}
          checkedColumns={checkedColumns}
          onCheck={onCheck}
          filters={filters}
          onOrderChange={onOrderChange}
          onColumnReorder={onColumnReorder}
          onFilterChange={onFilterChange}
          disabled={!editMode}
          viewMode={!editMode}
          showOrderControls={editMode}
          billType={type_name}
        />
      )}
    </Modal>
  );
};

export default TemplateDetailsModal;
